import './applicantEmploymentDetailsPage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import { toCurrencyString } from '@thesavingsgroup/utils/toCurrencyString';
import { toNumericString } from '@thesavingsgroup/utils/toNumericString';
import isEmpty from 'lodash/isEmpty';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { Input } from '../../../components/Input/Input';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useLeadChannelStore from '../../../store/leadChannelStore/useLeadChannelStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { phoneNumberWithCountryCode },
  } = useLeadChannelStore();
  const { next, previous } = useWorkflowStore();

  const {
    computed: { isCurrentIncomePersisted },
    currentIncomeEmployerName,
    currentIncomeGrossAnnual,
    currentIncomeJobTitle,
    isSyncingIncome,
    syncingIncomeFailedMessage,
    setCurrentIncomeEmployerName,
    setCurrentIncomeGrossAnnual,
    setCurrentIncomeJobTitle,
    syncIncome,
  } = useApplicantStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: 'onTouched',
    criteriaMode: 'firstError',
    defaultValues: {
      employerNameValue: currentIncomeEmployerName,
      jobTitleValue: currentIncomeJobTitle,
      grossAnnualIncomeValue: toCurrencyString(currentIncomeGrossAnnual),
    },
    delayError: 200,
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const employerNameValidation = {
    required: t('validation.employerName.required'),
  };

  const jobTitleValidation = {
    required: t('validation.jobTitle.required'),
  };

  const grossAnnualIncomeValidation = {
    setValueAs: (value: string): string => toNumericString(value),
    required: t('validation.grossAnnualIncome.required'),
    min: {
      value: 0,
      message: t('validation.grossAnnualIncome.min', { min: '$0' }),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { employerNameValue, jobTitleValue, grossAnnualIncomeValue } = values;

    if (!employerNameValue || !jobTitleValue || !grossAnnualIncomeValue) {
      return;
    }

    setCurrentIncomeEmployerName(employerNameValue);
    setCurrentIncomeGrossAnnual(grossAnnualIncomeValue);
    setCurrentIncomeJobTitle(jobTitleValue);

    if (isCurrentIncomePersisted && isDirty) {
      const synced = await syncIncome();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-employment-details-page" sx={theme.pageLayout}>
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.applicantEmploymentDetails.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={locator.pages.applicantEmploymentDetails.form.h1}
              >
                {t('ApplicantEmploymentDetailsPage.title')}
              </h1>
              <h2
                data-testid={locator.pages.applicantEmploymentDetails.form.h2}
              >
                {t('ApplicantEmploymentDetailsPage.subtitle')}
              </h2>

              {!isEmpty(syncingIncomeFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantEmploymentDetails.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingIncomeFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Input
                    {...register('employerNameValue', employerNameValidation)}
                    data-testid={
                      locator.pages.applicantEmploymentDetails.form
                        .employerNameInput
                    }
                    error={errors.employerNameValue}
                    errorTestId={
                      locator.pages.applicantEmploymentDetails.form
                        .employerNameInputError
                    }
                    label={t('ApplicantEmploymentDetailsPage.employerName')}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Input
                    {...register('jobTitleValue', jobTitleValidation)}
                    data-testid={
                      locator.pages.applicantEmploymentDetails.form
                        .jobTitleInput
                    }
                    error={errors.jobTitleValue}
                    errorTestId={
                      locator.pages.applicantEmploymentDetails.form
                        .jobTitleInputError
                    }
                    label={t('ApplicantEmploymentDetailsPage.jobTitle')}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <NumberInput
                    {...register(
                      'grossAnnualIncomeValue',
                      grossAnnualIncomeValidation,
                    )}
                    data-testid={
                      locator.pages.applicantEmploymentDetails.form
                        .grossAnnualIncomeInput
                    }
                    error={errors.grossAnnualIncomeValue}
                    errorTestId={
                      locator.pages.applicantEmploymentDetails.form
                        .grossAnnualIncomeInputError
                    }
                    label={t(
                      'ApplicantEmploymentDetailsPage.grossAnnualIncome',
                    )}
                    currency
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <Button
                  data-testid={
                    locator.pages.applicantEmploymentDetails.form.submitButton
                  }
                  disabled={!isValid}
                  loader={isSyncingIncome}
                  fullWidth
                  submit
                >
                  {t('ApplicantEmploymentDetailsPage.continue')}
                </Button>
              </div>

              <p
                className="form-disclosure"
                data-testid={locator.pages.applicantEmploymentDetails.form.incomeDisclosure}
              >
                <Trans
                  components={{
                    phone: <a href={`tel:${phoneNumberWithCountryCode}`} />,
                  }}
                  i18nKey="disclosures.applicantIncome"
                  values={{ phoneNumber: phoneNumberWithCountryCode }}
                />
              </p>
            </div>
            <footer
              data-testid={locator.pages.applicantEmploymentDetails.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantEmploymentDetailsPage';
